/**
 * The composer: where the SME answers.
 *
 * A capsule that stays on screen, with send and skip inside the field rather
 * than a row of buttons under it. It is `position: sticky` from the caller,
 * which is why the acrylic exists: the conversation scrolls underneath it, and
 * the design system sanctions a blur exactly where that happens.
 *
 * Suggestion chips sit above it and *fill* the box rather than sending, so an
 * answer is never put in someone's mouth — the mockup's chips typed into the
 * field for the same reason. Return sends; Shift + return starts a new line.
 */

import { useLayoutEffect, useRef } from 'react';
import { ds } from '../../ds';
import { EASE, RADIUS, SHADOW } from '../../theme';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onSkip: () => void;
  suggestions: string[];
  busy: boolean;
  /** The interviewer has what it needs; the finish action becomes available. */
  done: boolean;
  onFinish: () => void;
  placeholder: string;
}

export function Composer({
  value,
  onChange,
  onSend,
  onSkip,
  suggestions,
  busy,
  done,
  onFinish,
  placeholder
}: Props) {
  const { Button, IconButton, Tooltip } = ds();
  const box = useRef<HTMLTextAreaElement>(null);

  /**
   * Grow with the answer rather than scrolling inside three lines.
   *
   * Layout effect, not effect: measuring means collapsing the box to `auto`
   * first, and after paint that briefly shortens the document. The browser
   * clamps the scroll position to the shorter page and does not put it back
   * when the height returns, so on a conversation past one screen every
   * keystroke used to ratchet the page upward.
   */
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(200, Math.max(52, el.scrollHeight))}px`;
  }, [value]);

  return (
    <div>
      {suggestions.length > 0 && !value && (
        // One row that scrolls sideways rather than a wrapping block: the
        // composer is meant to take as little of the screen as it can.
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            paddingBottom: 10
          }}
        >
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="mv-acrylic"
              onClick={() => {
                onChange(s);
                box.current?.focus();
              }}
              style={{
                flex: 'none',
                border: '1px solid var(--border-strong)',
                borderRadius: RADIUS.pill,
                padding: '7px 13px',
                fontSize: 13,
                lineHeight: 1.3,
                color: 'var(--text-muted)',
                transition: `all 120ms ${EASE}`
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div
        className="mv-acrylic mv-composer"
        style={{
          borderRadius: RADIUS.panel,
          border: '1px solid var(--border-subtle)',
          boxShadow: SHADOW.hairline,
          padding: 10
        }}
      >
        <div style={{ position: 'relative' }}>
          <textarea
            ref={box}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && value.trim() && !busy) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={placeholder}
            rows={1}
            style={{
              display: 'block',
              width: '100%',
              minHeight: 52,
              maxHeight: 200,
              // The drag handle and the auto-grow fought: a resize survived
              // until the next keystroke measured over it.
              resize: 'none',
              overflowY: 'auto',
              border: 0,
              background: 'transparent',
              borderRadius: RADIUS.control,
              // Right gutter clears the two buttons.
              padding: '15px 84px 15px 14px',
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--text-body)',
              outline: 'none'
            }}
          />

          <div style={{ position: 'absolute', right: 4, bottom: 4, display: 'flex', gap: 2 }}>
            {/* `title={undefined}` drops the native tooltip the IconButton
                sets from `label`, so the two do not both appear. The
                aria-label is untouched. */}
            <Tooltip content="Skip this one">
              <IconButton
                icon="chevrons-right"
                label="Skip this one"
                title={undefined}
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={onSkip}
              />
            </Tooltip>
            <Tooltip content="Send · Enter">
              <IconButton
                icon="send"
                label="Send"
                title={undefined}
                size="sm"
                variant="primary"
                disabled={!value.trim() || busy}
                onClick={onSend}
              />
            </Tooltip>
          </div>
        </div>

        {/* Labelled, not an icon: it sends someone's work to a named person
            and there is no undo. Nothing else lives down here, so the row only
            exists once there is something in it. */}
        {done && (
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 6px 2px' }}>
            <Button variant="primary" size="sm" disabled={busy} onClick={onFinish}>
              Finish and send
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
